import {
  DailyAudio,
  DailyProvider,
  DailyVideo,
  useParticipantIds
} from '@daily-co/daily-react';
import { conversationStore } from 'entities/tavus/conversation';
import { DAILY_CALL_STATES, events } from 'features/daily-call/model';
import { dailyCallStore } from 'features/daily-call/model/dailyCallStore';
import { useEffect, Fragment, useRef } from 'react';
import { Loader, ToolTip } from 'shared/ui';
import {
  hotkeyDescriptions,
  TavusEmbedControlBar,
  useKeyboardFullScreenAndCaptionsBinding
} from 'widgets/call-controls';
import {
  EIntegrationSettingsType,
  tavusPersonaStore
} from 'entities/tavus/persona';
import { GreenScreenVideo } from 'features/green-screen-video';
import { Captions } from 'widgets/captions';
import { useTimerConversation } from 'entities/tavus/conversation/lib';
import { RiFullscreenExitLine } from 'react-icons/ri';
import { IoResizeOutline } from 'react-icons/io5';
import {
  cn,
  useFullScreen,
  useMouseVisibility,
  useQueryParams,
  logger
} from 'shared/lib';

type Props = {
  onCallEnd?: () => void;
};

export const TavusEmbedConference = ({ onCallEnd }: Props) => {
  const query = useQueryParams();
  const { currentConversation, endConversation, setStartStreamTime } =
    conversationStore((state) => ({
      currentConversation: state.currentConversation,
      endConversation: state.endConversation,
      setStartStreamTime: state.setStartStreamTime
    }));

  const key = query.get('key') || undefined;
  const type = query.get('type') as EIntegrationSettingsType;
  const disableFeatures = query.get('disableFeatures') === 'true';
  const { toggleFullScreen, isFullScreen, videoContainer } = useFullScreen();

  const currentPersona = tavusPersonaStore((state) => state.currentPersona);
  const windowFields =
    type === 'embed' ? currentPersona?.embed : currentPersona?.widget;

  const {
    startHairCheck,
    callObject,
    handleNewMeetingState,
    currentState,
    startLeavingCall,
    joinCall,
    toggleCaptions,
    withCaptions,
    messages,
    addMessage,
    stopRecording
  } = dailyCallStore();

  useEffect(() => {
    if (windowFields?.captions_by_default && !withCaptions) {
      toggleCaptions();
    }
  }, [windowFields, withCaptions, toggleCaptions]);

  useEffect(() => {
    if (currentConversation) {
      startHairCheck(currentConversation.conversation_url);
    }
  }, [currentConversation, startHairCheck]);

  useEffect(() => {
    if (!callObject) return;

    handleNewMeetingState();
    events.forEach((event) => callObject.on(event, handleNewMeetingState));

    const onTranscription = (message: any) => addMessage(message);
    callObject.on('transcription-message', onTranscription);

    const onError = (ev: any) => logger.error('Daily error event', ev);
    callObject.on('error', onError);

    return () => {
      events.forEach((event) => callObject.off(event, handleNewMeetingState));
      callObject.off('transcription-message', onTranscription);
      callObject.off('error', onError);
    };
  }, [callObject, handleNewMeetingState, addMessage]);

  useEffect(() => {
    if (!callObject) return;

    handleNewMeetingState();
    events.forEach((event) => callObject.on(event, handleNewMeetingState));

    const onTranscription = (m: any) => addMessage(m);
    callObject.on('transcription-message', onTranscription);

    let startedByThisEffect = false;
    const tryStartRecording = () => {
      const s = dailyCallStore.getState();
      if (
        !startedByThisEffect &&
        s.callObject?.meetingState() === 'joined-meeting' &&
        !s.isRecording
      ) {
        startedByThisEffect = true;
        s.startRecording();
      }
    };

    tryStartRecording();
    const onJoined = () => tryStartRecording();
    callObject.on('joined-meeting', onJoined);

    const onError = (ev: any) => logger.error('Daily error event', ev);
    callObject.on('error', onError);

    return () => {
      events.forEach((event) => callObject.off(event, handleNewMeetingState));
      callObject.off('transcription-message', onTranscription);
      callObject.off('joined-meeting', onJoined);
      callObject.off('error', onError);
    };
  }, [callObject, handleNewMeetingState, addMessage]);

  const handleEndCall = () => {
    stopRecording();
    if (currentConversation?.conversation_id) {
      endConversation(messages, key);
    }
    callObject?.destroy();
    startLeavingCall();
    onCallEnd?.();
  };

  useEffect(() => {
    if (currentState === DAILY_CALL_STATES.STATE_HAIRCHECK) {
      if (currentConversation) {
        joinCall(currentConversation?.conversation_url);
      }
    }
  }, [currentState, currentConversation, joinCall]);

  useEffect(() => {
    if (!callObject) return;

    handleNewMeetingState();
    events.forEach((event) => callObject.on(event, handleNewMeetingState));

    const onTranscription = (m: any) => addMessage(m);
    callObject.on('transcription-message', onTranscription);

    const onError = (ev: any) => logger.error('Daily error event', ev);
    callObject.on('error', onError);

    return () => {
      events.forEach((event) => callObject.off(event, handleNewMeetingState));
      callObject.off('transcription-message', onTranscription);
      callObject.off('error', onError);
    };
  }, [callObject, handleNewMeetingState, addMessage]);

  useTimerConversation(currentConversation, callObject);
  useKeyboardFullScreenAndCaptionsBinding({ toggleFullScreen, toggleCaptions });

  const isVisible = useMouseVisibility();

  if (!callObject) return null;

  return (
    <div
      ref={videoContainer}
      className="ag-flex ag-items-center ag-justify-center ag-relative ag-h-full"
    >
      <DailyProvider callObject={callObject}>
        {[
          DAILY_CALL_STATES.STATE_JOINING,
          DAILY_CALL_STATES.STATE_JOINED,
          DAILY_CALL_STATES.STATE_ERROR
        ].includes(currentState) ? (
          <div className="ag-w-full ag-h-full ag-flex ag-flex-col ag-items-center ag-justify-between">
            <Video disableFeatures={disableFeatures} />
            <div
              className={cn(
                'ag-flex ag-items-center ag-gap-6 ag-absolute ag-top-3.5 ag-right-3.5',
                isVisible ? 'ag-block' : 'ag-hidden'
              )}
            >
              <button
                className="ag-rounded-[10px] ag-bg-white-opacity-70 ag-p-2 ag-h-10 ag-w-10 ag-flex ag-flex-col ag-justify-center ag-text-dark-purple"
                onClick={toggleFullScreen}
              >
                {isFullScreen ? (
                  <RiFullscreenExitLine
                    color={windowFields?.dark_mode ? 'black' : 'white'}
                    size={24}
                  />
                ) : (
                  <IoResizeOutline
                    size={24}
                    color={windowFields?.dark_mode ? 'black' : 'white'}
                  />
                )}
                <ToolTip
                  className="ag-right-5 ag-top-5 ag-z-10 ag-text-white"
                  text={hotkeyDescriptions.toggleFullScreen}
                />
              </button>
            </div>
            <div className="ag-absolute ag-bottom-36 ag-left-0 ag-right-0 ag-z-[1000]">
              <Captions withCaptions={withCaptions} messages={messages} />
            </div>
            <TavusEmbedControlBar onCallEnd={handleEndCall} />
          </div>
        ) : (
          <div className="ag-w-full ag-h-screen ag-flex ag-items-center ag-justify-center">
            <Loader size={96} />
          </div>
        )}
        <DailyAudio />
      </DailyProvider>
    </div>
  );
};

const Video = ({ disableFeatures }: { disableFeatures: boolean }) => {
  const remoteParticipantIds = useParticipantIds({ filter: 'remote' });
  const ref = useRef<HTMLVideoElement>(null);
  const currentPersona = tavusPersonaStore((state) => state.currentPersona);

  return (
    <div className="ag-relative ag-w-full ag-h-full">
      {remoteParticipantIds.length > 0 ? (
        remoteParticipantIds.slice(0, 1).map((id) => (
          <Fragment key={id}>
            <DailyVideo
              className="ag-absolute ag-inset-0 ag-object-cover ag-w-full ag-h-full"
              ref={ref}
              automirror
              sessionId={id}
              type="video"
              playableStyle={{ width: '100%', height: '100%' }}
              fit="cover"
            />
            {!disableFeatures && (
              <GreenScreenVideo
                background={currentPersona?.custom_background}
                videoRef={ref}
                classNames="ag-relative ag-opacity-0 ag-w-full ag-h-full ag-inset-0"
              />
            )}
          </Fragment>
        ))
      ) : (
        <div className="ag-w-full ag-h-screen ag-flex ag-justify-center ag-items-center ag-pb-5 ag-flex-col">
          <Loader size={96} />
        </div>
      )}
    </div>
  );
};
